/**
 * SUB-4.16 · F-50's twin on `users`. What an assignee cell is allowed to say.
 *
 * ⚠️ THIS DECIDES WHAT IS SHOWN. IT BOUNDS NOTHING. Same standing rule as
 * `projectStageColumn.ts` and `projectListPaging.ts`: which people this caller
 * may name is decided by `merged_scope_user_names()` inside the database, and
 * nothing here may ever be why a name is absent.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ THE CARD RENDERED A RAW UUID AT THE USER.
 *
 * `CustomProjectCard` used to end its sales-rep branch with:
 *
 *     if ((project as any).assignedToId) {
 *       return `ID: ${(project as any).assignedToId}`;
 *     }
 *
 * That fires whenever the rep object is null, and in merged scope it is null for
 * every sub-account row: the board embeds
 * `sales_rep:users!assigned_sales_rep_id(...)`, a PostgREST embed is bounded by
 * the NARROWER of the two policies, and `users_select` is single-tenant while
 * `projects_select` resolves through merged scope. Identical mechanism to F-50
 * on `stages`, different table.
 *
 * ⚠️ THE PM BRANCH HAD THE OPPOSITE BUG, WHICH IS WORSE. It returned the string
 * `'Not Assigned'` for a PM it merely could not READ, so an unreadable PM and an
 * absent one were the same cell. `assigned_internal_pm_id` IS nullable, so both
 * states are real and telling them apart is the whole job of this module.
 *
 * ---------------------------------------------------------------------------
 * THE STATES, AND WHY THERE ARE EXACTLY THREE.
 *
 * Read from `information_schema` 2026-08-17:
 *
 *   projects.assigned_sales_rep_id          uuid NOT NULL
 *   projects.assigned_internal_pm_id        uuid NULL
 *   projects.assigned_proposal_designer_id  uuid NULL
 *
 * So `unassigned` is unreachable for a sales rep and reachable for the other
 * two. The same function serves all three; the column's nullability decides
 * which states can occur, and the caller does not have to know.
 */

import { getUserDisplayName, type UserRecord } from '@/utils/userUtils';

/** One row of `merged_scope_user_names(uuid[])`. */
export interface AssigneeRecord {
  user_id: string;
  tenant_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  is_active: boolean | null;
}

/**
 * ⚠️ A MAPPER, NOT A CAST, and the difference was a real compile error.
 * `AssigneeRecord` keys the person as `user_id` because that is what
 * `merged_scope_user_names` returns; `UserRecord` requires `id`. Casting between
 * them is what `tsc` rejects with TS2352, and `as unknown as UserRecord` would
 * have silenced it while leaving `id` undefined for anything that later read it.
 */
export function toUserRecord(record: AssigneeRecord): UserRecord {
  return {
    id: record.user_id,
    first_name: record.first_name,
    last_name: record.last_name,
    email: record.email,
    avatar_url: record.avatar_url,
    is_active: record.is_active ?? undefined,
  };
}

export type AssigneeCell =
  | { kind: 'named'; name: string; record: AssigneeRecord }
  | { kind: 'unassigned' }
  | { kind: 'unavailable' };

/**
 * ⚠️ LOAD-BEARING COPY, NOT CHROME. These two strings are the only place the
 * product distinguishes "nobody is assigned" from "somebody is assigned and we
 * could not read who", and the whole of this story is that the card said the
 * first when it meant the second, or printed a uuid. Asserted in
 * `assigneeName.test.ts` so a tidy-up of "just wording" fails a test.
 */
export const ASSIGNEE_UNASSIGNED_LABEL = 'Unassigned';
export const ASSIGNEE_UNASSIGNED_NOTE = 'Nobody is assigned to this project in this role.';

export const ASSIGNEE_UNAVAILABLE_LABEL = 'Unavailable';
export const ASSIGNEE_UNAVAILABLE_NOTE =
  'Somebody is assigned, but their details could not be read. This is not the same as nobody being assigned.';

/**
 * The cell for one assignee slot.
 *
 * `readFailed` is the CALL's outcome, not the row's: a lookup that errored makes
 * every assignee on that page unavailable, because a page with no answer must
 * not be rendered as a page whose answer is "nobody".
 *
 * ⚠️ A MISSING RECORD IS `unavailable`, NEVER `unassigned` AND NEVER A UUID. The
 * id is present, so somebody IS assigned; the only unknown is who.
 */
export function assigneeCellFor(
  assigneeId: string | null | undefined,
  record: AssigneeRecord | undefined,
  readFailed: boolean,
): AssigneeCell {
  // No id means the column is genuinely empty. This is the ONLY route to
  // `unassigned`, and it is unreachable for a sales rep because that column is
  // NOT NULL.
  if (!assigneeId) return { kind: 'unassigned' };

  if (readFailed) return { kind: 'unavailable' };
  if (!record) return { kind: 'unavailable' };

  // `getUserDisplayName` stays the ONE definition of how a person is named,
  // including the `[Inactive]` suffix and the `deleted+` email rule. A second
  // definition here is how a card comes to disagree with a dropdown.
  const name = getUserDisplayName(toUserRecord(record)).trim();

  // "Unknown User" is what `getUserDisplayName` returns when it has a record but
  // nothing nameable in it. That is not a name, and showing it would be the same
  // failure in a third costume.
  if (name === '' || name === 'Unknown User') return { kind: 'unavailable' };

  return { kind: 'named', name, record };
}

/** What the cell renders. ⚠️ Never an id, in any state. */
export function assigneeCellLabel(cell: AssigneeCell): string {
  switch (cell.kind) {
    case 'named':
      return cell.name;
    case 'unassigned':
      return ASSIGNEE_UNASSIGNED_LABEL;
    case 'unavailable':
      return ASSIGNEE_UNAVAILABLE_LABEL;
  }
}

/** The cell's explanation, or null when the name speaks for itself. */
export function assigneeCellNote(cell: AssigneeCell): string | null {
  switch (cell.kind) {
    case 'named':
      return null;
    case 'unassigned':
      return ASSIGNEE_UNASSIGNED_NOTE;
    case 'unavailable':
      return ASSIGNEE_UNAVAILABLE_NOTE;
  }
}

/**
 * The page's people, keyed by user id.
 *
 * ⚠️ MERGE, NEVER REPLACE, at the call site, for the same reason
 * `stageRowsById` says so: the board loads stages incrementally and a map
 * rebuilt from the newest batch alone would blank everything already on screen.
 */
export function assigneeRecordsById(
  rows: readonly AssigneeRecord[],
): Map<string, AssigneeRecord> {
  const byId = new Map<string, AssigneeRecord>();
  for (const row of rows) {
    if (row && typeof row.user_id === 'string') byId.set(row.user_id, row);
  }
  return byId;
}

/**
 * Is this a row of `merged_scope_user_names`?
 *
 * ⚠️ PRESENCE, NOT NON-NULLNESS, and a per-row test rather than the `every()`
 * that cost SUB-4.11 a story: a malformed row must cost that person their name,
 * never the whole page its assignees.
 */
export function isAssigneeRecord(value: unknown): value is AssigneeRecord {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return typeof row.user_id === 'string' && 'first_name' in row && 'last_name' in row;
}
